import { DataObfuscator } from "@/modules/obfuscator";
import { MAP_CONFIGS, MapType } from "./useMap";
import pako from "pako";
import { MyHouseData, SimpleTilemapJSON, MyHouseTile } from "../types/furnish";
import { FurnishComponentType } from "../enums/furnishEnums";

const FLOOR_TEXTURE_KEY = "floor-atlas";
const WALL_TEXTURE_KEY = "wall-atlas";
const FURNITURE_TEXTURE_KEY = "furniture-atlas";

export const useMyHouseMap = (scene: Phaser.Scene, json: MyHouseData) => {
  const config = MAP_CONFIGS[MapType.MY_HOUSE];
  const { worldSettings, floorData, wallData, furnitureData } = json;

  // 缓存：动画帧全量键名 与 动画配置（便于 load 阶段解析、create 阶段注册、objects 阶段播放）
  let animationFramesByAssetKey: Record<string, string[]> = {};
  let animationConfigByAssetKey: Record<
    string,
    { frameRate: number; loop: boolean }
  > = {};

  const load = () => {
    // 统一按配置加载三类图集
    (
      [
        { key: FLOOR_TEXTURE_KEY, img: floorData?.userAtlas?.imagePath },
        { key: WALL_TEXTURE_KEY, img: wallData?.userAtlas?.imagePath },
        {
          key: FURNITURE_TEXTURE_KEY,
          img: furnitureData?.userAtlas?.imagePath,
        },
        ...(furnitureData?.customTextures?.map((item) => ({
          key: item.key,
          img: item.image,
        })) ?? []),
      ] as const
    )
      .filter(({ img }) => img)
      .forEach(({ key, img }) => {
        scene.textures.remove(key);
        scene.load.image(key, img);
      });
    // 碰撞 tileset 图片
    scene.load.image("collision_32x32", "collision_32x32.png");

    // 预处理动画：收集每个 assetKey 的帧键名（与配置）
    animationFramesByAssetKey = {};
    animationConfigByAssetKey = {};
    if (furnitureData?.animations && furnitureData?.userAtlas?.frames) {
      const framesDict = furnitureData.userAtlas.frames;
      Object.entries(furnitureData.animations).forEach(([assetKey, def]) => {
        const fullKeys: string[] = [];
        def.frames.forEach((short) => {
          const hit = framesDict[`${assetKey}__${short}`];
          if (hit) fullKeys.push(`${assetKey}__${short}`);
        });
        if (fullKeys.length > 0) {
          animationFramesByAssetKey[assetKey] = fullKeys;
          const frameRate = def.config?.frameRate ?? 10;
          const loop = def.config?.loop ?? true;
          animationConfigByAssetKey[assetKey] = { frameRate, loop };
        }
      });
    }
  };

  /**
   * Convert house-dev SaveData to Phaser Tilemap-compatible format
   * This creates a proper tilemap data structure that Phaser can use
   *
   * Handles negative grid coordinates by:
   * 1. Finding the full coordinate range (min and max for both X and Y)
   * 2. Calculating map dimensions to span the entire range
   * 3. Applying coordinate transformation: arrayIndex = gridCoord - minCoord
   * 4. Returning the coordinate offset for use in positioning other objects
   */
  const convertToTilemapData = (
    saveData: MyHouseData
  ): {
    tilemapJSON: SimpleTilemapJSON;
    coordinateOffset: { x: number; y: number };
  } => {
    const { worldSettings, floorData, wallData } = saveData;
    const gridSize = worldSettings.gridSize || 32;

    // First, find the actual map dimensions by iterating through all tiles
    let minGridX = Infinity;
    let minGridY = Infinity;
    let maxGridX = -Infinity;
    let maxGridY = -Infinity;

    // Check floor tiles for min/max dimensions
    if (floorData?.tiles) {
      floorData.tiles.forEach((tile) => {
        minGridX = Math.min(minGridX, tile.gridX);
        minGridY = Math.min(minGridY, tile.gridY);
        maxGridX = Math.max(maxGridX, tile.gridX);
        maxGridY = Math.max(maxGridY, tile.gridY);
      });
    }

    // Check wall tiles for min/max dimensions
    if (wallData?.tiles) {
      wallData.tiles.forEach((tile) => {
        minGridX = Math.min(minGridX, tile.gridX);
        minGridY = Math.min(minGridY, tile.gridY);
        maxGridX = Math.max(maxGridX, tile.gridX);
        maxGridY = Math.max(maxGridY, tile.gridY);
      });
    }

    // Handle case where no tiles exist
    if (minGridX === Infinity) {
      minGridX = 0;
      minGridY = 0;
      maxGridX = 0;
      maxGridY = 0;
    }

    // Calculate actual map dimensions spanning the full coordinate range
    const mapWidth = maxGridX - minGridX + 1;
    const mapHeight = maxGridY - minGridY + 1;

    // Initialize 2D arrays for tile data with actual dimensions
    const floorTileData: number[][] = Array(mapHeight)
      .fill(null)
      .map(() => Array(mapWidth).fill(-1));
    const wallTileData: number[][] = Array(mapHeight)
      .fill(null)
      .map(() => Array(mapWidth).fill(-1));

    // Process floor tiles

    if (floorData?.tiles) {
      const atlasWidth = scene.textures
        .get(FLOOR_TEXTURE_KEY)
        .getSourceImage().width;
      floorData.tiles.forEach((tile) => {
        // Convert materialId to tile index
        // MaterialId can be a string like "0", "1", etc.
        const { x: tileX, y: tileY } =
          floorData.userAtlas!.materials![tile.materialId];

        // Calculate tile index based on atlas dimensions
        const tilesPerRow = Math.floor(atlasWidth / gridSize);
        const tileIndex = tileX / gridSize + (tileY / gridSize) * tilesPerRow;

        // Apply coordinate transformation: array indices = grid coordinates - min coordinates
        const arrayX = tile.gridX - minGridX;
        const arrayY = tile.gridY - minGridY;

        if (
          !isNaN(tileIndex) &&
          arrayY >= 0 &&
          arrayY < mapHeight &&
          arrayX >= 0 &&
          arrayX < mapWidth
        ) {
          floorTileData[arrayY][arrayX] = tileIndex;
        }
      });
    }

    // Process wall tiles
    if (wallData?.tiles) {
      const atlasWidth = scene.textures
        .get(WALL_TEXTURE_KEY)
        .getSourceImage().width;
      wallData.tiles.forEach((tile) => {
        const { x: tileX, y: tileY } =
          wallData.userAtlas!.materials![tile.materialId];

        // Calculate tile index based on atlas dimensions
        const tilesPerRow = Math.floor(atlasWidth / gridSize);
        const tileIndex = tileX / gridSize + (tileY / gridSize) * tilesPerRow;

        // Apply coordinate transformation: array indices = grid coordinates - min coordinates
        const arrayX = tile.gridX - minGridX;
        const arrayY = tile.gridY - minGridY;

        if (
          !isNaN(tileIndex) &&
          arrayY >= 0 &&
          arrayY < mapHeight &&
          arrayX >= 0 &&
          arrayX < mapWidth
        ) {
          wallTileData[arrayY][arrayX] = tileIndex;
        }
      });
    }

    // Create Phaser tilemap JSON format
    const tilemapJSON: SimpleTilemapJSON = {
      width: mapWidth,
      height: mapHeight,
      tilewidth: gridSize,
      tileheight: gridSize,
      layers: [
        {
          name: "floor",
          width: mapWidth,
          height: mapHeight,
          data: floorTileData, // Convert 2D array to 1D
          visible: true,
          opacity: 1,
          x: 0,
          y: 0,
        },
        {
          name: "wall",
          width: mapWidth,
          height: mapHeight,
          data: wallTileData,
          visible: true,
          opacity: 1,
          x: 0,
          y: 0,
        },
      ],
      tilesets: [] as unknown[],
    };

    // Add tileset info if atlases exist
    if (floorData?.userAtlas) {
      const floorMaterials = Object.keys(floorData.userAtlas.materials || {});
      tilemapJSON.tilesets.push({
        name: "floor-tileset",
        firstgid: 0,
        tilewidth: gridSize,
        tileheight: gridSize,
        tilecount: floorMaterials.length,
        columns: Math.ceil(Math.sqrt(floorMaterials.length)),
        image: "floor-atlas",
      });
    }

    if (wallData?.userAtlas) {
      const wallMaterials = Object.keys(wallData.userAtlas.materials || {});
      const floorTileCount = floorData?.userAtlas
        ? Object.keys(floorData.userAtlas.materials || {}).length
        : 0;
      tilemapJSON.tilesets.push({
        name: "wall-tileset",
        firstgid: floorTileCount,
        tilewidth: gridSize,
        tileheight: gridSize,
        tilecount: wallMaterials.length,
        columns: Math.ceil(Math.sqrt(wallMaterials.length)),
        image: "wall-atlas",
      });
    }

    return {
      tilemapJSON,
      coordinateOffset: {
        x: minGridX,
        y: minGridY,
      },
    };
  };

  const create = () => {
    const gridSize = worldSettings.gridSize || 32;

    // Add furniture atlas frames if atlas exists
    if (
      scene.textures.exists(FURNITURE_TEXTURE_KEY) &&
      furnitureData?.userAtlas?.frames
    ) {
      const furnitureTexture = scene.textures.get(FURNITURE_TEXTURE_KEY);
      // Add each frame to the texture
      Object.entries(furnitureData.userAtlas.frames).forEach(
        ([frameName, frameData]) => {
          const frame = frameData as {
            x: number;
            y: number;
            w: number;
            h: number;
          };
          if (frame.w > 0 && frame.h > 0) {
            furnitureTexture.add(
              frameName,
              0,
              frame.x,
              frame.y,
              frame.w,
              frame.h
            );
          }
        }
      );
    }

    // 注册动画（依赖于 load 阶段生成的缓存 + 上面添加的帧）
    if (Object.keys(animationFramesByAssetKey).length > 0) {
      Object.entries(animationFramesByAssetKey).forEach(
        ([assetKey, fullFrameKeys]) => {
          const config = animationConfigByAssetKey[assetKey] ?? {
            frameRate: 10,
            loop: true,
          };
          const animKey = `${assetKey}_anim`;
          // 避免重复注册
          scene.anims.remove(animKey);
          scene.anims.create({
            key: animKey,
            frames: fullFrameKeys.map((frame) => ({
              key: FURNITURE_TEXTURE_KEY,
              frame,
            })),
            frameRate: config.frameRate,
            repeat: config.loop ? -1 : 0,
          });
        }
      );
    }

    // Convert save data to tilemap format
    const { tilemapJSON: tilemapData, coordinateOffset } =
      convertToTilemapData(json);

    // scene.cache.tilemap.add(MapType.MY_HOUSE, { format: 1, data: tilemapData });

    // Create tilemap from converted data
    const map = scene.make.tilemap({
      key: undefined,
      tileWidth: tilemapData.tilewidth,
      tileHeight: tilemapData.tileheight,
      width: tilemapData.width,
      height: tilemapData.height,
    });

    // 以配置驱动的 tileset 注册
    const tilesets: Record<string, Phaser.Tilemaps.Tileset | null> = {
      floor: scene.textures.exists(FLOOR_TEXTURE_KEY)
        ? map.addTilesetImage(
            "floor-tileset",
            FLOOR_TEXTURE_KEY,
            gridSize,
            gridSize
          )
        : null,
      wall: scene.textures.exists(WALL_TEXTURE_KEY)
        ? map.addTilesetImage(
            "wall-tileset",
            WALL_TEXTURE_KEY,
            gridSize,
            gridSize
          )
        : null,
    };

    // Create layers from the data（统一 floor / wall）
    const layers: Phaser.Tilemaps.TilemapLayer[] = [];
    (
      [
        { name: "floor", depth: 10, dataIndex: 0, collides: false },
        { name: "wall", depth: 50, dataIndex: 1, collides: true },
      ] as const
    ).forEach(({ name, depth, dataIndex, collides }) => {
      const ts = tilesets[name];
      const layerObj = tilemapData.layers[dataIndex];
      const layerData = layerObj ? layerObj.data : undefined;
      if (!ts || !layerData) return;
      const layer = map.createBlankLayer(name, ts)!;

      let tileIdx = 0;
      for (let y = 0; y < layerData.length; y++) {
        for (let x = 0; x < layerData[y].length; x++) {
          const idx = layerData[y][x];
          if (idx >= 0) {
            const tile = layer.putTileAt(idx, x, y);
            let originData: MyHouseTile | null = null;
            if (name === "floor") {
              originData = json["floorData"]!.tiles![tileIdx];
            } else if (name === "wall") {
              originData = json["wallData"]!.tiles![tileIdx];
            }
            if (originData) {
              tile.setFlipX(originData.scaleX < 0);
              tile.setFlipY(originData.scaleY < 0);
              if (originData.rotation !== 0) {
                tile.rotation = originData.rotation;
              }
            }
          }
          tileIdx++;
        }
      }
      if (config.scale !== 1) layer.setScale(config.scale);
      if (collides) layer.setCollisionByExclusion([-1]);
      // Trick: 直接设置depth会被Phaser重置为0，因此延时设置
      setTimeout(() => {
        layer.setDepth(depth);
      }, 100);
      layers.push(layer);
    });

    return {
      map,
      tileLayers: layers,
      coordinateOffset,
    };
  };

  const createObjects = (
    map: Phaser.Tilemaps.Tilemap,
    coordinateOffset: { x: number; y: number } = { x: 0, y: 0 }
  ) => {
    const furnitureSprites: Phaser.GameObjects.Sprite[] = [];

    // 创建家具对象 (furniture can't be in tilemap, keep as sprites)
    json.entities.forEach((entity) => {
      const position = entity.components[FurnishComponentType.Position];
      const renderable = entity.components[FurnishComponentType.Renderable];
      const furniture = entity.components[FurnishComponentType.Furniture];
      const rotation = entity.components[FurnishComponentType.Rotation];
      const scale = entity.components[FurnishComponentType.Scale];
      const zIndex = entity.components[FurnishComponentType.ZIndex];
      const color = entity.components[FurnishComponentType.Color];

      if (!position || !renderable || !furniture) {
        return;
      }

      // Determine texture key and frame name
      const baseAssetKey = renderable.assetKey as string | number;
      let frameName = (renderable.frame ?? renderable.assetKey) as
        | string
        | number;

      // DO NOT apply coordinate offset - furniture positions are already in world pixel coordinates
      // The coordinate offset is only for tile grid-to-array index transformation
      // 如果该 assetKey 存在动画，设置首帧为动画的第一帧
      let initialFrameForAnim: string | undefined;
      if (
        typeof baseAssetKey === "string" &&
        animationFramesByAssetKey[baseAssetKey]?.length
      ) {
        initialFrameForAnim = animationFramesByAssetKey[baseAssetKey][0];
        frameName = initialFrameForAnim;
      }

      let furnitureSprite: Phaser.GameObjects.Sprite;
      if (typeof frameName === "string" && frameName.startsWith("custom_")) {
        furnitureSprite = scene.add.sprite(
          position.x - coordinateOffset.x * map.tileWidth,
          position.y - coordinateOffset.y * map.tileHeight,
          renderable.assetKey,
          0
        );
      } else {
        furnitureSprite = scene.add.sprite(
          position.x - coordinateOffset.x * map.tileWidth,
          position.y - coordinateOffset.y * map.tileHeight,
          FURNITURE_TEXTURE_KEY,
          frameName // Use the frame name directly, as we've already added frames to the texture
        );
      }

      // Set origin first before transforms
      furnitureSprite.setOrigin(0.5, 0.5);
      furnitureSprite.setScale(scale?.x ?? 1, scale?.y ?? 1);
      furnitureSprite.setRotation(rotation?.angle ?? 0);
      furnitureSprite.setVisible(renderable.visible ?? true);
      furnitureSprite.setAlpha(1);

      // Apply color tint if Color component exists
      if (color) {
        let tintValue: number | null = null;

        if (typeof color.tint === "number" && !Number.isNaN(color.tint)) {
          tintValue = color.tint;
        } else if (typeof color.value === "string") {
          const colorValue = color.value.trim();
          const hexString = colorValue.startsWith("#")
            ? colorValue.slice(1)
            : colorValue;
          const parsed = parseInt(hexString, 16);
          tintValue = Number.isNaN(parsed) ? null : parsed;
        }

        if (tintValue != null) {
          furnitureSprite.setTint(tintValue);
        }
      }

      // Apply Y-sort depth: base(100) + zIndex + bottomY
      furnitureSprite.setDepth(zIndex?.value ?? 100);

      // 如存在动画，播放动画（优先使用组件里的 animationKey，否则使用约定 `${assetKey}_anim`）
      if (
        typeof baseAssetKey === "string" &&
        animationFramesByAssetKey[baseAssetKey]?.length
      ) {
        const compAnimKey = furniture?.animationKey;
        const animKey = compAnimKey ?? `${baseAssetKey}_anim`;
        if (scene.anims.exists(animKey)) {
          furnitureSprite.play(animKey);
        }
      }

      furnitureSprites.push(furnitureSprite);
    });

    return furnitureSprites;
  };

  // 基于 wall 瓦片 + 家具精灵构建隐藏的碰撞层（供 GridEngine 使用）
  const createCollisionLayer = (
    map: Phaser.Tilemaps.Tilemap,
    sprites?: Phaser.GameObjects.Sprite[]
  ) => {
    const gridSize = worldSettings.gridSize || 32;

    const mapWidth = map.width;
    const mapHeight = map.height;

    // 选择 collision tileset：优先复用已存在的，再考虑用 collision.png 创建
    const tileset: Phaser.Tilemaps.Tileset = map.addTilesetImage(
      "collision-tileset",
      "collision_32x32",
      gridSize,
      gridSize
    )!;

    // 构建占用网格（false=可走，true=阻塞）
    const occupied: boolean[][] = Array(mapHeight)
      .fill(null)
      .map(() => Array(mapWidth).fill(false));

    // 1) 墙体：直接从已创建的 Tilemap 的 wall 层读取是否存在 tile
    const wallLayer = map.getLayer("wall")?.tilemapLayer;
    if (wallLayer) {
      for (let y = 0; y < mapHeight; y++) {
        for (let x = 0; x < mapWidth; x++) {
          const t = wallLayer.getTileAt(x, y);
          if (t && t.index >= 0) occupied[y][x] = true;
        }
      }
    }

    // 2) 家具：优先使用传入的 sprites（静态物体），避免重复解析 JSON
    if (sprites && sprites.length > 0) {
      sprites.forEach((spr) => {
        const b = spr.getBounds();
        const minGX = Math.floor(b.left / gridSize);
        const maxGX = Math.floor((b.right - 1) / gridSize);
        const minGY = Math.floor(b.top / gridSize);
        const maxGY = Math.floor((b.bottom - 1) / gridSize);
        for (let gy = minGY; gy <= maxGY; gy++) {
          for (let gx = minGX; gx <= maxGX; gx++) {
            if (gy >= 0 && gy < mapHeight && gx >= 0 && gx < mapWidth) {
              occupied[gy][gx] = true;
            }
          }
        }
      });
    }

    // 创建 collision 层
    const collisionLayer = map.createBlankLayer("collision", tileset)!;
    collisionLayer.setAlpha(0.5);

    const blockingTileIndex = 13;

    for (let y = 0; y < mapHeight; y++) {
      for (let x = 0; x < mapWidth; x++) {
        if (occupied[y][x]) {
          const tile = collisionLayer.putTileAt(blockingTileIndex, x, y);
          tile.properties["ge_collide"] = true;
        } else {
          const tile = collisionLayer.putTileAt(0, x, y);
          tile.properties["ge_collide"] = false;
        }
      }
    }

    collisionLayer.setVisible(false);
    collisionLayer.setDepth(9999);

    return collisionLayer;
  };

  // 解析 floor 与 collision 图层，缓存出生点 tiles
  let cachedSpawnTiles: { x: number; y: number }[] | null = null;

  const getRandomSpawnTile = (
    map: Phaser.Tilemaps.Tilemap
  ): { x: number; y: number } | null => {
    if (!cachedSpawnTiles) {
      const floorLayer = map.getLayer("floor")?.tilemapLayer;
      const collisionLayer = map.getLayer("collision")?.tilemapLayer;

      const spawnables: { x: number; y: number }[] = [];

      const mapWidth = map.width;
      const mapHeight = map.height;

      for (let y = 0; y < mapHeight; y++) {
        for (let x = 0; x < mapWidth; x++) {
          const floorTile = floorLayer?.getTileAt(x, y);
          const hasFloor = !!floorTile && floorTile.index >= 0;
          if (!hasFloor) continue;

          let blocked = false;
          if (collisionLayer) {
            const cTile = collisionLayer.getTileAt(x, y);
            if (cTile) blocked = cTile.properties["ge_collide"] === true;
          }

          if (!blocked) spawnables.push({ x, y });
        }
      }

      cachedSpawnTiles = spawnables;
    }

    if (!cachedSpawnTiles || cachedSpawnTiles.length === 0) return null;
    const idx = Math.floor(Math.random() * cachedSpawnTiles.length);
    return cachedSpawnTiles[idx];
  };

  const invalidateSpawnCache = () => {
    cachedSpawnTiles = null;
  };

  return {
    load,
    create,
    createObjects,
    createCollisionLayer,
    getRandomSpawnTile,
    invalidateSpawnCache,
    mapScale: config.scale,
  };
};

export const decodeData = (encodedString: string): string | undefined => {
  const isObfuscatedData = DataObfuscator.isObfuscated(encodedString);

  try {
    const deobfuscatedData = DataObfuscator.deobfuscate(encodedString);
    try {
      const jsonString = decompressFromBase64(deobfuscatedData);
      return jsonString;
    } catch {
      const jsonString = atob(deobfuscatedData);
      return jsonString;
    }
  } catch {
    if (isObfuscatedData) {
      throw new Error("Save data format is invalid or corrupted");
    }
    // Fall through to try non-obfuscated format
  }
};

const decompressFromBase64 = (base64: string): string => {
  const binaryData = decodeBase64(base64);
  return pako.ungzip(binaryData, { to: "string" });
};

const decodeBase64 = (base64: string): Uint8Array => {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};
